import React, { useState, useRef, useReducer } from 'react'
import PropTypes from 'prop-types'
import { mapValues } from 'lodash'
import { Container, Heading } from '@scentregroup/ui'
import classNames from 'classnames'
import { useQueryParams, ArrayParam, StringParam } from 'use-query-params'
import WhatsOnList from '../../../components/whats-on-list'
import { WhatsOnSeeMoreContext } from '../../../context'
import Modules from '../../../components/modules'
import { getNowInCentreInDateFormat } from '../../../helpers/date-helpers'
import {
  rangesFromOccurrencesListsAndFilters,
  getOcccurrencesLists,
} from '../../../components/whats-on-list/contentful-to-ranges'
import WhatsHappeningFilters from '../../../components/whats-happening-filters'
import EventNotification from '../../../components/event-notification'
import { filterRanges, makeMultiFilterProps } from './helper'
import styles from './index.module.scss'

const QUERY_NAME = {
  DATE_RANGE: 'range',
  CONTENT_TYPES: 'types',
  TOPICS: 'topics',
  EXPERIENCES: 'experiences',
}

const CONTENT_TYPES_FILTERS = ['Events', 'Offers']

function reducer(state, action) {
  switch (action.type) {
    case 'expand_see_more':
      return { ...state, [action.range]: true }
    case 'collapse_see_more':
      if (action.range) {
        return { ...state, [action.range]: false }
      }
      return mapValues(state, _item => false)
    default:
      return { ...state }
  }
}

export function WhatsHappening(props) {
  const {
    events,
    offers,
    centre,
    template,
    modules,
    topicFilters,
    experienceFilters,
  } = props

  const [state, dispatch] = useReducer(reducer, {})
  const [query, setQuery] = useQueryParams({
    range: StringParam,
    types: ArrayParam,
    topics: ArrayParam,
    experiences: ArrayParam,
  })
  const { range: dateRange, types: contentTypes, topics, experiences } = query
  const makeSetter = paramName => val =>
    setQuery({ [paramName]: val }, 'pushIn')
  const [setDateRange, setContentTypes, setTopics, setExperiences] = [
    QUERY_NAME.DATE_RANGE,
    QUERY_NAME.CONTENT_TYPES,
    QUERY_NAME.TOPICS,
    QUERY_NAME.EXPERIENCES,
  ].map(makeSetter)
  const onResetFilters = () => {
    dispatch({ type: 'collapse_see_more' })
    return setQuery({}, 'push')
  }
  const { current: baseDate } = useRef(
    getNowInCentreInDateFormat(centre.timeZone)
  )

  const [occurrencesLists] = useState(() =>
    getOcccurrencesLists(baseDate, events, offers, centre)
  )

  // Props for the filters
  const contentTypesProps = makeMultiFilterProps(
    contentTypes,
    setContentTypes,
    CONTENT_TYPES_FILTERS
  )
  const topicsProps = makeMultiFilterProps(topics, setTopics, topicFilters)
  const experiencesProps = makeMultiFilterProps(
    experiences,
    setExperiences,
    experienceFilters
  )
  const dateRangeProps = {
    baseDate,
    selectedDateRange: dateRange,
    onChange: setDateRange,
    dataTestId: 'date-range-filters',
  }

  // Values of selected filters to do the actual filtering
  const whatsOnContentId = 'whats-on-content'
  const whatsOnList = filterRanges(
    rangesFromOccurrencesListsAndFilters(baseDate, occurrencesLists, {
      contentTypes,
      experiences,
      topics,
      dateRange,
    }),
    dateRange
  )
  const heading = "What's happening"
  return (
    <Container
      type="large"
      className={classNames(styles.whatsHappeningContainer, styles.evolved)}
    >
      <Heading
        type="medium"
        className={classNames(
          styles.whatsHappeningHeading,
          styles.whatsHappeningPageHeading
        )}
      >
        {heading}
      </Heading>
      <EventNotification className={styles.warningNotification} />
      <Modules modules={modules || []} />
      <>
        <div className={styles.whatsOn}>
          <div
            aria-controls={whatsOnContentId}
            className={styles.whatsOnFilters}
          >
            <WhatsHappeningFilters
              {...{
                dateRangeProps,
                onResetFilters,
                contentTypesProps,
                topicsProps,
                experiencesProps,
              }}
            />
          </div>
          <WhatsOnSeeMoreContext.Provider value={{ state, dispatch }}>
            <WhatsOnList
              ranges={whatsOnList}
              analyticsDisplayId={template?.analyticsMetadata?.displayId}
            />
          </WhatsOnSeeMoreContext.Provider>
        </div>
      </>
    </Container>
  )
}

WhatsHappening.propTypes = {
  topicFilters: PropTypes.array,
  experienceFilters: PropTypes.array,
  events: PropTypes.array,
  offers: PropTypes.array,
  centre: PropTypes.object,
  template: PropTypes.shape({
    analyticsMetadata: PropTypes.shape({
      displayId: PropTypes.string,
    }),
  }),
  modules: PropTypes.array,
}

WhatsHappening.defaultPropTypes = {
  modules: [],
}
